/**
 * Shared concepts toolbar + grid for concept groups (OR-6126).
 *
 * Rendered identically by the preview Concepts tab and the created
 * concept-group detail so the two **look and operate the same**: an optional
 * search box (OR-6288: the design drops the All / Standard / Non-standard
 * toggle), a CSV download, and a compact server-paginated
 * {@link ProjectDataGrid}. Purely presentational — it takes a table *model*
 * (search / filter / paging / download state) so each surface keeps its own
 * data hook (``usePreviewConceptsTable`` vs ``useConceptGroupConceptsTable``)
 * and the data source stays out of the component.
 *
 * TODO: cleanup with research_assistant_gen_3 ff
 */

import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  getGridStringOperators,
  type GridColDef,
  type GridFilterModel,
  type GridSortModel,
} from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { Icon } from '@verantos-dev/shared-components';

import { ProjectDataGrid } from './ProjectDataGrid';
import type { PreviewStandardFilter } from '../conceptStandardFilter';

/** The minimal concept row both surfaces' rows satisfy structurally. */
export interface ConceptRow {
  conceptId: string | number;
  conceptName?: string | null;
  conceptCode?: string | null;
  vocabularyId?: string | null;
  standardConcept?: string | null;
  domainId?: string | null;
}

/** View-state contract both concepts hooks return (preview + created). */
export interface ConceptsTableModel {
  searchInput: string;
  setSearchInput: (value: string) => void;
  standardFilter: PreviewStandardFilter;
  setStandardFilter: (value: PreviewStandardFilter) => void;
  /** 0-based page index, for the grid pagination model. */
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  /** Server-side sort model (single column) — drives the ``sort`` query param. */
  sortModel: GridSortModel;
  onSortModelChange: (model: GridSortModel) => void;
  /** Server-side column filter (single column) — drives the ``filter`` param. */
  filterModel: GridFilterModel;
  onFilterModelChange: (model: GridFilterModel) => void;
  concepts: ConceptRow[];
  total: number;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  downloading: boolean;
  handleDownload: () => Promise<void>;
}

export interface ConceptsExplorerProps {
  model: ConceptsTableModel;
  /** Prefix for test ids (e.g. ``cg-preview`` / ``concept-group-detail``). */
  testIdPrefix: string;
  /** Disables the download (e.g. before a preview id is known). */
  canDownload?: boolean;
  /** Show the toolbar's download button. The draft Concepts tab hides it (its
   *  metadata strip above carries the download instead); the saved
   *  concept-group view keeps it. */
  showDownload?: boolean;
  /** Show the toolbar's search box. The draft Concepts tab hides it and
   *  renders {@link ConceptsSearchField} on its metadata strip instead, next
   *  to the download icon. */
  showSearch?: boolean;
}

/** The concepts search field, exported so the draft tab's metadata strip can
 *  host the SAME control (same model + testid) next to its download icon. */
export const ConceptsSearchField = ({
  model,
  testIdPrefix,
}: {
  model: ConceptsTableModel;
  testIdPrefix: string;
}) => {
  const { formatMessage } = useIntl();
  return (
    <TextField
      size="small"
      placeholder={formatMessage({ id: 'epiChat.concepts.searchPlaceholder' })}
      value={model.searchInput}
      onChange={(e) => model.setSearchInput(e.target.value)}
      sx={{
        flex: 1,
        minWidth: 200,
        maxWidth: 420,
        '& .MuiOutlinedInput-root': { height: 36 },
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" />
          </InputAdornment>
        ),
      }}
      inputProps={{ 'data-testid': `${testIdPrefix}-search-input` }}
    />
  );
};

export const ConceptsExplorer = ({
  model,
  testIdPrefix,
  canDownload = true,
  showDownload = true,
  showSearch = true,
}: ConceptsExplorerProps) => {
  const { formatMessage } = useIntl();

  const standardYesLabel = formatMessage({ id: 'epiChat.concepts.standardValue.yes' });
  const standardNoLabel = formatMessage({ id: 'epiChat.concepts.standardValue.no' });

  // Only the operators both concepts endpoints honor server-side (the created
  // repository + the preview SQL builder). Excludes value-less operators
  // (isEmpty / isAnyOf) the backends ignore.
  const stringOperators = useMemo(
    () =>
      getGridStringOperators().filter((op) =>
        ['contains', 'doesNotContain', 'equals', 'doesNotEqual', 'startsWith', 'endsWith'].includes(
          op.value
        )
      ),
    []
  );

  const columns = useMemo<GridColDef<ConceptRow>[]>(
    () => [
      {
        field: 'conceptId',
        headerName: formatMessage({ id: 'epiChat.concepts.col.id' }),
        flex: 1,
        minWidth: 110,
        // Only Concept name is sortable for now (server-side single-column sort).
        sortable: false,
        filterOperators: stringOperators,
        valueGetter: (_value, row) => String(row.conceptId),
      },
      {
        field: 'conceptName',
        headerName: formatMessage({ id: 'epiChat.concepts.col.name' }),
        flex: 2,
        minWidth: 200,
        // The one sortable column: the header toggles conceptName:asc <-> desc,
        // sent to the server (see the grid's sortingMode="server" below).
        sortable: true,
        filterOperators: stringOperators,
        valueGetter: (_value, row) => row.conceptName ?? '—',
      },
      // OR-6288: column order follows the Figma — Standard sits before
      // Vocabulary, and Concept code after it.
      {
        field: 'standardConcept',
        headerName: formatMessage({ id: 'epiChat.concepts.col.standard' }),
        flex: 1,
        minWidth: 110,
        // Standard yes/no flag (matches the mapping-results "Standard" column);
        // 'S' → Yes, else No. The All/Standard/Non-standard toggle owns this
        // filter, and a column filter would match the raw 'S' not the label —
        // so keep it non-sortable / non-filterable.
        sortable: false,
        filterable: false,
        valueGetter: (_value, row) =>
          row.standardConcept === 'S' ? standardYesLabel : standardNoLabel,
      },
      {
        field: 'vocabularyId',
        headerName: formatMessage({ id: 'epiChat.concepts.col.vocabulary' }),
        flex: 1,
        minWidth: 110,
        sortable: false,
        filterOperators: stringOperators,
        valueGetter: (_value, row) => row.vocabularyId ?? '—',
      },
      {
        field: 'conceptCode',
        headerName: formatMessage({ id: 'epiChat.concepts.col.code' }),
        flex: 1,
        minWidth: 110,
        sortable: false,
        filterOperators: stringOperators,
        valueGetter: (_value, row) => row.conceptCode ?? '—',
      },
      {
        // OR-6125: Domain column for parity with the legacy mapping/upload review surfaces.
        // Flex-sized + non-filterable, matching the sibling derived "Type" column.
        field: 'domainId',
        headerName: formatMessage({ id: 'epiChat.concepts.col.domain' }),
        flex: 1,
        minWidth: 110,
        sortable: false,
        filterable: false,
        valueGetter: (_value, row) => row.domainId ?? '—',
      },
    ],
    [formatMessage, standardYesLabel, standardNoLabel, stringOperators]
  );

  return (
    <Stack sx={{ gap: 1.5, height: '100%', minHeight: 0 }} data-testid={`${testIdPrefix}-concepts`}>
      {/* OR-6288: the All / Standard / Non-standard toggle is dropped per the
          design. Search and download are both optional — the draft tab hosts
          them on its metadata strip instead; the saved view keeps them here. */}
      {(showSearch || showDownload) && (
        <Stack direction="row" sx={{ gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {showSearch && <ConceptsSearchField model={model} testIdPrefix={testIdPrefix} />}
          <Box sx={{ flex: 1 }} />
          {showDownload && (
            <Tooltip title={formatMessage({ id: 'epiChat.concepts.downloadTooltip' })}>
              <span>
                <IconButton
                  size="small"
                  onClick={model.handleDownload}
                  disabled={model.downloading || !canDownload}
                  aria-label={formatMessage({ id: 'epiChat.concepts.downloadAria' })}
                  data-testid={`${testIdPrefix}-csv-button`}
                >
                  {model.downloading ? (
                    <CircularProgress size={16} />
                  ) : (
                    <Icon id={`${testIdPrefix}-csv-icon`} icon="download" width={20} height={20} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Stack>
      )}

      {model.isError ? (
        <Typography variant="body2" color="error" data-testid={`${testIdPrefix}-concepts-error`}>
          {formatMessage({ id: 'epiChat.concepts.loadError' })}
        </Typography>
      ) : (
        <ProjectDataGrid<ConceptRow>
          id={`${testIdPrefix}-concepts`}
          rows={model.concepts}
          columns={columns}
          getRowId={(row) => String(row.conceptId)}
          loading={model.isLoading || model.isFetching}
          density="compact"
          pagination
          paginationMode="server"
          rowCount={model.total}
          paginationModel={{ page: model.page, pageSize: model.pageSize }}
          onPaginationModelChange={(next) => model.setPage(next.page)}
          pageSizeOptions={[model.pageSize]}
          // Server-side single-column sort: only the Concept name header is
          // sortable (see columns). Clicking it toggles asc <-> desc — no
          // unsorted third state — and the model resets to page 1 and refetches.
          // The kebab menu stays hidden; sorting is header-click only.
          sortingMode="server"
          sortModel={model.sortModel}
          onSortModelChange={model.onSortModelChange}
          sortingOrder={['asc', 'desc']}
          disableColumnMenu
          noRowsLabel={formatMessage({ id: 'epiChat.concepts.empty' })}
        />
      )}
    </Stack>
  );
};
